import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  fetchWithRetry,
} from '@/lib/apiRequest';

export type AttentionItem = {
  id:
    string;

  source:
    'exception' |
    'deadline' |
    'task' |
    'communication' |
    'lead' |
    'financial';

  title:
    string;

  detail:
    string |
    null;

  severity:
    'critical' |
    'important' |
    'normal';

  riskLevel:
    string |
    null;

  href:
    string;

  occurredAt:
    string |
    null;

  dueAt:
    string |
    null;

  clientId:
    string |
    null;

  caseId:
    string |
    null;
};

export type AttentionSummary = {
  total:
    number;

  critical:
    number;

  important:
    number;

  normal:
    number;
};

export type AttentionAggregation = {
  complete: boolean;
  warning?: string;
  failedSources: string[];
};

const emptySummary:
  AttentionSummary = {
  total:
    0,

  critical:
    0,

  important:
    0,

  normal:
    0,
};

export function useAttentionCenter(
  options: {
    limit?:
      number;

  } =
    {}
) {
  const limit =
    options.limit ??
    50;

  const [
    items,
    setItems,
  ] =
    useState<
      AttentionItem[]
    >([]);

  const [
    summary,
    setSummary,
  ] =
    useState<
      AttentionSummary
    >(
      emptySummary
    );

  const [
    generatedAt,
    setGeneratedAt,
  ] =
    useState<
      string |
      null
    >(
      null
    );

  const [
    loading,
    setLoading,
  ] =
    useState(
      true
    );

  const [
    error,
    setError,
  ] =
    useState<
      string |
      null
    >(
    null
  );

  const [aggregation, setAggregation] = useState<AttentionAggregation>({ complete: true, failedSources: [] });

  const load =
    useCallback(
      async () => {
        setLoading(
          true
        );

        try {
          const response =
            await fetchWithRetry(
              `/api/attention?limit=${encodeURIComponent(
                String(
                  limit
                )
              )}`
            );

          const data =
            await response
              .json() as {
              success?:
                boolean;

              error?:
                string;

              items?:
                AttentionItem[];

              summary?:
                AttentionSummary;

              generatedAt?:
                string;

              aggregation?: AttentionAggregation;
            };

          if (
            !response.ok ||
            !data.success ||
            !Array.isArray(
              data.items
            ) ||
            !data.summary
          ) {
            throw new Error(
              data.error ||
              'Falha ao carregar Central de Atenção.'
            );
          }

          const nextAggregation = data.aggregation ?? { complete: true, failedSources: [] };
          const failedSources = new Set(nextAggregation.failedSources);
          setItems((currentItems) => {
            const preservedItems = currentItems.filter((item) => failedSources.has(item.source === 'financial' ? 'financial' : `${item.source}s`));
            return [...data.items, ...preservedItems];
          });

          setSummary((currentSummary) => nextAggregation.complete ? data.summary : currentSummary);
          setAggregation(nextAggregation);

          setGeneratedAt(
            data.generatedAt ??
            null
          );

          setError(
            null
          );
        } catch (
          loadError
        ) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Falha ao carregar Central de Atenção.'
          );
        } finally {
          setLoading(
            false
          );
        }
      },
      [
        limit,
      ]
    );

  useEffect(
    () => {
      void load();
    },
    [
      load,
    ]
  );

  return {
    items,
    summary,
    aggregation,
    generatedAt,
    loading,
    error,
    load,
  };
}
